import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { EditSaveCancel } from '@/components/ui/edit-save-cancel';

type OrganizationOption = {
    organization_uuid: string;
    name: string;
    type: string;
};

interface OrganizationSelectionCardProps {
    title: string;
    description: string;
    organizations: OrganizationOption[];
    activeOrganizationUuid?: string | null;
    defaultOrganizationUuid?: string | null;
    helperText?: string;
    emptyStateText?: string;
    sortDefaultFirst?: boolean;
    onSubmit: (organizationUuid: string) => Promise<void>;
}

export function OrganizationSelectionCard({
    title,
    description,
    organizations,
    activeOrganizationUuid,
    defaultOrganizationUuid,
    helperText,
    emptyStateText = 'No organization selected',
    sortDefaultFirst = false,
    onSubmit,
}: OrganizationSelectionCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedOrgId, setSelectedOrgId] = useState<string>('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [displayOrgUuid, setDisplayOrgUuid] = useState<string | null>(
        activeOrganizationUuid ?? null
    );

    useEffect(() => {
        setDisplayOrgUuid(activeOrganizationUuid ?? null);
    }, [activeOrganizationUuid]);

    const currentOrg = organizations.find((org) => org.organization_uuid === displayOrgUuid);

    const sortedOrganizations = useMemo(() => {
        const list = [...organizations];
        if (sortDefaultFirst && defaultOrganizationUuid) {
            return list.sort((a, b) => {
                if (a.organization_uuid === defaultOrganizationUuid) return -1;
                if (b.organization_uuid === defaultOrganizationUuid) return 1;
                return a.name.localeCompare(b.name);
            });
        }
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [organizations, sortDefaultFirst, defaultOrganizationUuid]);

    const canEdit = organizations.length > 0;
    const showEditControls = canEdit;

    const determineInitialSelection = () => {
        const fallback =
            activeOrganizationUuid ??
            defaultOrganizationUuid ??
            organizations[0]?.organization_uuid ??
            null;
        return fallback ?? '';
    };

    const startEditing = () => {
        if (!canEdit) {
            return;
        }
        setSelectedOrgId(determineInitialSelection());
        setIsEditing(true);
        setErrorMessage(null);
    };

    useEffect(() => {
        if (!isEditing) {
            setSelectedOrgId('');
            setErrorMessage(null);
        }
    }, [isEditing]);

    const handleCancel = () => {
        setIsEditing(false);
        setSelectedOrgId('');
        setErrorMessage(null);
    };

    const handleSave = async () => {
        if (!selectedOrgId) {
            setErrorMessage('Please select an organization.');
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);

        try {
            await onSubmit(selectedOrgId);
            setDisplayOrgUuid(selectedOrgId);
            setIsEditing(false);
        } catch (error) {
            setErrorMessage(
                error instanceof Error ? error.message : 'Failed to update organization'
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                {showEditControls && (
                    <EditSaveCancel
                        onEdit={startEditing}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isEditing={isEditing}
                        isLoading={isSaving}
                    />
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {helperText && <p className="text-sm text-muted-foreground">{helperText}</p>}

                {!canEdit && (
                    <p className="text-sm text-muted-foreground">
                        You are not assigned to any organizations.
                    </p>
                )}

                {canEdit && !isEditing && (
                    <div>
                        {currentOrg ? (
                            <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                    <p className="font-medium">{currentOrg.name}</p>
                                    <Badge variant="secondary">{currentOrg.type}</Badge>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">{emptyStateText}</p>
                        )}
                    </div>
                )}

                {canEdit && isEditing && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor={`${title}-selector`}>Select Organization</Label>
                            <Select
                                value={selectedOrgId}
                                onValueChange={(value) => {
                                    setSelectedOrgId(value);
                                    setErrorMessage(null);
                                }}
                            >
                                <SelectTrigger id={`${title}-selector`}>
                                    <SelectValue placeholder="Choose organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sortedOrganizations.map((org) => (
                                        <SelectItem
                                            key={org.organization_uuid}
                                            value={org.organization_uuid}
                                        >
                                            {org.name} ({org.type})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {errorMessage && (
                            <Alert variant="destructive">
                                <AlertDescription>{errorMessage}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
